import React, { lazy, Suspense, useEffect } from "react";
import { createPortal } from "react-dom";

// FCP/LCP 优化：把 RescueFundVipLevelContent / RescueFundNetLossContent 改成
// React.lazy。两个 Content 组件各自约 30-35KB raw（含 webp 资源 + 业务逻辑），
// 加上各自 import 的 dayjs（替换 moment 后 ~10KB raw）。原本通过这两行静态 import：
//   useRescueFundPopup → RescueFundPopup → 静态 import RescueFund 二级页内容
// 把整页代码从注定二级路由的 chunk 拖进了首屏共享 chunk（即 PageLoadingContext-*.js）。
// 改 lazy 后，只有 RescueFundPopup 真正 render（visible=true 且 type 命中）时
// 才下载对应 chunk，首屏 chunk 立即缩水。
//
// 命名导出 → React.lazy 的标准包装：用 `.then(m => ({ default: m.X }))`。
const RescueFundVipLevelContent = lazy(() =>
  import("../../../../pages/RescueFund/RescueFundVipLevel").then(m => ({
    default: m.RescueFundVipLevelContent,
  }))
);
const RescueFundNetLossContent = lazy(() =>
  import("../../../../pages/RescueFund/RescueFundNetLoss").then(m => ({
    default: m.RescueFundNetLossContent,
  }))
);

export interface RescueFundPopupProps {
  visible: boolean;
  onClose: () => void;
  taskId: string;
  type: "VIP_LEVEL" | "NET_LOSS";
  /** 每次打开弹窗时递增，作为内容 key 使组件重新挂载，从而实时请求上一期活动数据 */
  refreshKey?: number;
  /** 领取成功后调用，用于刷新浮窗角标（让数字 1 消失） */
  onClaimSuccess?: () => void;
}

export const RescueFundPopup: React.FC<RescueFundPopupProps> = ({
  visible,
  onClose,
  taskId,
  type,
  refreshKey = 0,
  onClaimSuccess,
}) => {
  // ✅ 确保弹窗关闭时清理滚动锁定
  useEffect(() => {
    if (!visible) {
      // 弹窗关闭时，强制恢复页面滚动
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      console.log("[RescueFundPopup] 弹窗关闭，已清理滚动锁定");
    }
  }, [visible]);

  // ✅ 组件卸载时也清理
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      console.log("[RescueFundPopup] 组件卸载，已清理滚动锁定");
    };
  }, []);

  if (!visible) {
    return null;
  }

  const handleSetTaskId = (id: string | null) => {
    if (!id) {
      onClose();
    }
  };

  const content =
    type === "VIP_LEVEL" ? (
      <RescueFundVipLevelContent
        key={refreshKey}
        taskId={taskId}
        setTaskId={handleSetTaskId}
        onClaimSuccess={onClaimSuccess}
      />
    ) : type === "NET_LOSS" ? (
      <RescueFundNetLossContent
        key={refreshKey}
        taskId={taskId}
        setTaskId={handleSetTaskId}
        onClaimSuccess={onClaimSuccess}
      />
    ) : null;

  // 使用 Portal 挂到 body，完全脱离 #root/.phone-wrapper，避免任意父级 overflow 裁切左右边缘。
  // Suspense 必须放在 Portal 内部：lazy chunk 加载期间 fallback 渲染在 body 下，
  // 不会回退到任何祖先 Suspense（从而避免误触发外层骨架/loading）。
  // fallback=null：弹窗本就是用户点击触发，~50-200ms 的下载窗口不显示占位最自然。
  return createPortal(<Suspense fallback={null}>{content}</Suspense>, document.body);
};

export default RescueFundPopup;
